import React from "react";
import {
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    LinearProgress,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from "@material-ui/core";
import ThresholdForm from "./ThresholdForm";
import { v4 as uuidv4 } from "uuid";
import { toast } from "react-toastify";
import { api_host } from "../../../../common/globals/constants";
import { useConfiguracionLimites } from "../hooks/useConfiguracionLimites";
import ThresholdItem from "./ThesholdItem";
import CloseIcon from "@mui/icons-material/Close";
import { withTranslation } from 'react-i18next';

const SET_INITIAL_STATE = "setInitialState";
const SET_STATE = "setState";

const limitesReducer = (state, action) => {
    switch (action.type) {
        case SET_STATE:
            return action.data;
        case SET_INITIAL_STATE:
            return action.data;
        default:
            return [];
    }
};

const colors = [
    "#8fe3dc",
    "#97b588",
    "#4596a1",
    "#3b71b8",
    "#9336c2",
    "#bf4bb4",
    "#e3448e",
    "#9c8467",
    "#dacc1d",
    "#545454",
];

function ConfiguracionColoresUmbral({
    showModal,
    setShowModal,
    t,
}) {
    const [limites, dispatch] = React.useReducer(limitesReducer, []);
    const [isSendingData, setIsSendingData] = React.useState(false);
    const { data, isLoading, isSuccess } = useConfiguracionLimites();

    React.useEffect(() => {
        if (isSuccess) {
            let initialState = data.map((item) => ({
                id: uuidv4(),
                ...item,
                disabled: true,
                canBeDeleted:
                    Number(item.limite) !== 100 && Number(item.limite) !== 0,
            }));
            if (!initialState.length) {
                initialState = [
                    {
                        id: uuidv4(),
                        limite: 0,
                        color: "#C2272D",
                        disabled: true,
                        canBeDeleted: false,
                    },
                    {
                        id: uuidv4(),
                        limite: 100,
                        color: "#41B900",
                        disabled: true,
                        canBeDeleted: false,
                    },
                ];
            }
            dispatch({ type: SET_INITIAL_STATE, data: initialState });
        }
    }, [isSuccess]);

    const availableColors = React.useMemo(() => {
        return colors.filter(
            (color) => limites.findIndex((item) => item.color === color) === -1
        );
    }, [limites]);

    function deleteLimite(id) {
        const configuracionLimites = limites.filter((item) => item.id !== id);
        saveConfiguracionLimites(configuracionLimites);
    }

    async function saveConfiguracionLimites(configuracionLimites) {
        setIsSendingData(true);
        try {
            const token = sessionStorage.getItem("key");
            const clienteId = sessionStorage.getItem("client_id");
            const response = await toast.promise(
                fetch(
                    `${api_host}/reporte/online/indicadores/updateConfiguracionLimites/`,
                    {
                        method: "PATCH",
                        body: JSON.stringify({
                            token,
                            clienteId: clienteId,
                            configuracionLimites,
                        }),
                        headers: { "Content-Type": "application/json" },
                    }
                ),
                {
                    pending: t('thresholdConfig.toast.pending'),
                }
            );

            if (!response.ok) {
                throw new Error();
            }
            toast.success(t('thresholdConfig.toast.saveSuccess'));
            dispatch({ type: SET_STATE, data: configuracionLimites });
        } catch (error) {
            toast.error(t('thresholdConfig.toast.errorSave'));
        } finally {
            setIsSendingData(false);
        }
    }

    function addLimite(limite, color) {
        if (limite <= 0 || limite >= 100) {
            toast.error(t('thresholdConfig.add.invalid'));
            return;
        }
        if (limites.findIndex((item) => item.limite === limite) > -1) {
            toast.error(t('thresholdConfig.add.exists'));
            return;
        }
        const configuracionLimites = [
            ...limites,
            {
                id: uuidv4(),
                limite,
                color,
                disabled: true,
                canBeDeleted: true,
            },
        ].sort((a, b) => a.limite - b.limite);

        saveConfiguracionLimites(configuracionLimites);
    }

    function handleClose() {
        if (!isSendingData) {
            setShowModal(false);
        }
    }

    return (
        <Dialog open={showModal} maxWidth="xs" fullWidth onClose={handleClose}>
            <DialogTitle disableTypography={true}>
                <Typography variant="h4">
                    {t('thresholdConfig.title')}
                </Typography>
                <IconButton
                    aria-label={t('thresholdConfig.button.closeAria')}
                    style={{
                        position: "absolute",
                        right: 5,
                        top: 5,
                    }}
                    onClick={handleClose}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent style={{ padding: 0 }}>
                {isSendingData || (isLoading && <LinearProgress />)}
                <Table size="small" className="at-custom-form-table">
                    <TableHead>
                        <TableRow>
                            <TableCell colSpan={2}>{t('thresholdConfig.table.header.limit')}</TableCell>
                            <TableCell colSpan={2}>{t('thresholdConfig.table.header.color')}</TableCell>
                            <TableCell></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {limites.map((item) => (
                            <TableRow key={item.id}>
                                <ThresholdItem
                                    id={item.id}
                                    limite={item.limite}
                                    color={item.color}
                                    deleteLimite={
                                        item.canBeDeleted ? deleteLimite : false
                                    }
                                    disabled={item.disabled}
                                />
                            </TableRow>
                        ))}
                        <TableRow>
                            <ThresholdForm
                                addItem={addLimite}
                                colors={availableColors}
                                disabled={isLoading}
                            />
                        </TableRow>
                    </TableBody>
                </Table>
            </DialogContent>
        </Dialog>
    );
}
 
export default withTranslation()(ConfiguracionColoresUmbral);
